import { ScrollArea, Skeleton } from "@mantine/core";
import IconSparkles from "@src/assets/icons/lucide/sparkles.svg?react";
import IconBrandWhatsapp from "@src/assets/icons/tabler/brand-whatsapp.svg?react";
import IconFolder from "@src/assets/icons/tabler/folder.svg?react";
import IconPencilMinus from "@src/assets/icons/tabler/pencil.svg?react";
import { ROUTES } from "@src/common/constants";
import { WhatsAppIntegrationModalButton } from "@src/components/modals/whatsapp-integration-modal/whatsapp-integration-modal";
import { FavouritesPanel } from "@src/components/navigation/nav-section-tree/favourites-panel";
import {
	NavSectionTabs,
	type NavTreeTab,
} from "@src/components/navigation/nav-section-tree/nav-section-tabs";
import {
	NavTreeCreateBoardRow,
	NavTreePanel,
} from "@src/components/navigation/nav-section-tree/nav-tree-panel";
import { SharedTreePanel } from "@src/components/navigation/nav-section-tree/shared-tree-panel";
import { NativeLikeTooltip as Tooltip } from "@src/components/ui/native-like-tooltip";
import { NavbarSection } from "@src/gql/graphql";
import clsx from "clsx";
import { useCallback, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { NavLink, useLocation } from "react-router-dom";
import { MentionInboxBell } from "../mentions/inbox/mention-inbox-bell";
import { useAuthenticatedUser } from "../user/user-context";
import { NavActiveSourceProvider } from "./context/nav-active-source-context";
import { NavBarHandlerProvider } from "./context/nav-bar-handler-context";
import { NavPendingRenameProvider } from "./context/nav-pending-rename-context";
import { SearchProvider } from "./context/search-context";
import { NavBarFooter } from "./nav-bar-footer";
import { NavBarSearchComponent, useShowNavBarSearch } from "./nav-bar-search";
import NavSectionErrorItem from "./nav-section-panel/nav-section-error-item";
import { UserButton } from "./user-button";

const ACTIVE_TAB_STORAGE_KEY = "nav-tree-active-tab";

function getInitialNavTab(): NavTreeTab {
	if (typeof window === "undefined") return "workspace";
	try {
		const stored = localStorage.getItem(ACTIVE_TAB_STORAGE_KEY);
		if (stored === "workspace" || stored === "shared" || stored === "pinned") {
			return stored;
		}
	} catch {
		// Ignore storage read error
	}
	return "workspace";
}

function sectionForTab(tab: NavTreeTab): NavbarSection {
	return tab === "shared"
		? NavbarSection.SharedWithMe
		: NavbarSection.MyWhiteboards;
}

const UserButtonSkeleton = ({ className }: { className?: string }) => (
	<div className="flex w-full min-h-10 items-center justify-between gap-1.5">
		<div
			className={clsx(
				"block min-w-0 max-w-full flex-1 rounded-lg",
				"border border-solid border-border-subtle",
				className,
			)}
		>
			<div className="flex flex-row items-center">
				<Skeleton height={34} width={34} circle className="mr-2 shrink-0" />
				<div className="flex min-w-0 grow flex-col gap-0.5">
					<Skeleton height={13} width={80} />
					<Skeleton height={11} width={60} />
				</div>
			</div>
		</div>
		<Skeleton height={30} width={30} radius="md" className="shrink-0" />
	</div>
);

function NavBarContent({ activeTab }: { activeTab: NavTreeTab }) {
	return (
		<div className="px-3">
			{activeTab === "pinned" && (
				<ErrorBoundary
					fallback={<NavSectionErrorItem label="Failed to load pinned items" />}
				>
					<FavouritesPanel hideSectionHeader />
				</ErrorBoundary>
			)}

			{activeTab === "workspace" && (
				<ErrorBoundary
					fallback={<NavSectionErrorItem label="Failed to load boards" />}
				>
					<NavTreePanel hideSectionHeader />
				</ErrorBoundary>
			)}

			{activeTab === "shared" && (
				<ErrorBoundary
					fallback={
						<NavSectionErrorItem label="Failed to load shared boards" />
					}
				>
					<SharedTreePanel hideSectionHeader />
				</ErrorBoundary>
			)}
		</div>
	);
}

function NavSearchCreateRow({ activeTab }: { activeTab: NavTreeTab }) {
	const showSearch = useShowNavBarSearch();

	return (
		<div className="flex items-center gap-1.5 px-3">
			{showSearch ? (
				<div className="flex-1 min-w-0">
					<NavBarSearchComponent />
				</div>
			) : null}
			<ErrorBoundary
				fallback={<NavSectionErrorItem label="Failed to load board actions" />}
			>
				<div className={showSearch ? "shrink-0" : "w-full"}>
					<NavTreeCreateBoardRow
						compact={showSearch}
						section={sectionForTab(activeTab)}
					/>
				</div>
			</ErrorBoundary>
		</div>
	);
}

interface NavBarProps {
	innerScroll?: boolean;
}

export function NavBar({ innerScroll = true }: NavBarProps) {
	const authenticatedUser = useAuthenticatedUser();
	const location = useLocation();
	const [activeTab, setActiveTab] = useState<NavTreeTab>(getInitialNavTab);

	const handleTabChange = useCallback((tab: NavTreeTab) => {
		setActiveTab(tab);
		try {
			localStorage.setItem(ACTIVE_TAB_STORAGE_KEY, tab);
		} catch {
			// Ignore storage write error
		}
	}, []);

	const isWhatsAppConnected = Boolean(authenticatedUser?.phoneNumber);
	const isDraftActive = location.pathname.startsWith(ROUTES.draft);
	const isLibraryActive = location.pathname.startsWith(ROUTES.library);
	const isHomeActive = location.pathname === ROUTES.home;

	return (
		<nav className="flex h-full min-h-0 flex-col gap-1 overflow-y-auto bg-surface-default">
			<div className="flex flex-col gap-1 px-3 pt-1">
				{authenticatedUser ? (
					<UserButton className="p-1" />
				) : (
					<UserButtonSkeleton className="p-1" />
				)}

				<div className="relative h-8 w-full">
					<Tooltip label="Ask Illumi" openDelay={300} withArrow>
						<NavLink
							to={ROUTES.home}
							aria-label="Ask"
							className={clsx(
								"absolute inset-0 flex flex-row flex-nowrap items-center",
								"gap-2.5 w-full h-8 pl-2.5 pr-2 rounded-lg text-xs",
								"font-semibold transition-colors select-none",
								isHomeActive
									? "bg-interaction-selected text-brand-primary"
									: "text-text-primary hover:bg-surface-hover-subtle",
							)}
						>
							<IconSparkles width={15} height={15} className="shrink-0" />
							<span className="truncate leading-normal">Ask</span>
						</NavLink>
					</Tooltip>
				</div>

				<div className="relative h-8 w-full">
					<Tooltip label="Draft notes and scratchpad" openDelay={300} withArrow>
						<NavLink
							to={ROUTES.draft}
							aria-label="Draft"
							className={clsx(
								"absolute inset-0 flex flex-row flex-nowrap items-center",
								"gap-2.5 w-full h-8 pl-2.5 pr-8 rounded-lg text-xs",
								"font-semibold transition-colors select-none",
								isDraftActive
									? "bg-interaction-selected text-brand-primary"
									: "text-text-primary hover:bg-surface-hover-subtle",
							)}
						>
							<IconPencilMinus width={15} height={15} className="shrink-0" />
							<span className="truncate leading-normal">Draft</span>
						</NavLink>
					</Tooltip>
					<div className="pointer-events-none absolute inset-y-0 right-2 flex items-center">
						{isWhatsAppConnected ? (
							<Tooltip label="WhatsApp connected" openDelay={300} withArrow>
								<span
									aria-label="WhatsApp connected"
									className="pointer-events-auto h-1.75 w-1.75 shrink-0 rounded-full bg-whatsapp-primary"
								/>
							</Tooltip>
						) : (
							<WhatsAppIntegrationModalButton source="navbar">
								<Tooltip label="Connect WhatsApp" openDelay={300} withArrow>
									<button
										type="button"
										title="Connect WhatsApp"
										aria-label="Connect WhatsApp"
										className={clsx(
											"flex h-6 w-6 shrink-0 cursor-pointer items-center",
											"justify-center rounded-md text-whatsapp-primary",
											"pointer-events-auto transition-colors",
											"hover:bg-whatsapp-primary/15",
										)}
									>
										<IconBrandWhatsapp
											width={17}
											height={17}
											className="shrink-0"
										/>
									</button>
								</Tooltip>
							</WhatsAppIntegrationModalButton>
						)}
					</div>
				</div>

				<div className="relative h-8 w-full">
					<Tooltip label="Card Library" openDelay={300} withArrow>
						<NavLink
							to={ROUTES.library}
							aria-label="Card Library"
							className={clsx(
								"absolute inset-0 flex flex-row flex-nowrap items-center",
								"gap-2.5 w-full h-8 pl-2.5 pr-8 rounded-lg text-xs",
								"font-semibold transition-colors select-none",
								isLibraryActive
									? "bg-interaction-selected text-brand-primary"
									: "text-text-primary hover:bg-surface-hover-subtle",
							)}
						>
							<IconFolder width={15} height={15} className="shrink-0" />
							<span className="truncate leading-normal">Library</span>
						</NavLink>
					</Tooltip>
					<div className="pointer-events-none absolute inset-y-0 right-2 flex items-center">
						<div className="pointer-events-auto flex items-center justify-center">
							<MentionInboxBell size="xs" variant="navbar" />
						</div>
					</div>
				</div>
			</div>

			<SearchProvider>
				<NavActiveSourceProvider>
					<NavBarHandlerProvider>
						<NavPendingRenameProvider>
							<div className="flex min-h-0 flex-1 flex-col gap-1">
								<NavSearchCreateRow activeTab={activeTab} />
								<NavSectionTabs
									activeTab={activeTab}
									onTabChange={handleTabChange}
								/>
								{innerScroll ? (
									<ScrollArea
										scrollbarSize={4}
										type="scroll"
										className="min-h-0 flex-1"
									>
										<NavBarContent activeTab={activeTab} />
									</ScrollArea>
								) : (
									<div className="min-h-0 flex-1 overflow-y-auto">
										<NavBarContent activeTab={activeTab} />
									</div>
								)}
							</div>
						</NavPendingRenameProvider>
					</NavBarHandlerProvider>
				</NavActiveSourceProvider>
			</SearchProvider>

			<NavBarFooter />
		</nav>
	);
}
